"use client";

import { FormEvent, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { learningStageForSkill, learningStages } from "@/lib/learning-stages";
import type { Topic } from "@/lib/api";
import { candidateSelectors, generatorCandidates, type GeneratorCandidate } from "@/lib/generator-candidates";
import { filterChoices, validSeed, type CatalogChoice } from "@/lib/generator-filter";
import { apiBaseUrl } from "@/lib/browser-api";
import { catalogGenerators } from "@/lib/topic-generators";
import { allPdfOutputs, togglePdfOutput, type PdfOutput } from "@/lib/pdf-outputs";

type Learner = { id: string; display_name: string; grade: number | null };

function cookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`))
    ?.split("=")[1];
}

export default function GeneratorForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const requestedSkill = searchParams.get("skill");
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [pdfOutputs, setPdfOutputs] = useState<PdfOutput[]>([...allPdfOutputs]);
  const [outputMessage, setOutputMessage] = useState<string>();
  const [skill, setSkill] = useState("");
  const [seed, setSeed] = useState("17421");
  const [learners, setLearners] = useState<Learner[]>([]);
  const [candidates, setCandidates] = useState<GeneratorCandidate[]>([]);
  const [catalog, setCatalog] = useState<CatalogChoice[]>([]);
  const [catalogState, setCatalogState] = useState<"loading" | "ready" | "error">("loading");
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  const [collection, setCollection] = useState<"regular" | "demo">("regular");
  const mathCandidates = candidates.filter(c=>c.subject === "mathematik");
  const choices: CatalogChoice[] = collection === "regular" ? mathCandidates : catalog.filter(c=>!mathCandidates.some(a=>a.skillId===c.skillId));
  const visible = filterChoices(choices, query, subject, grade);
  const selected = candidates.find((item) => item.skillId === skill);
  const stage = learningStages[learningStageForSkill(skill)];

  useEffect(() => {
    const initialLoad = window.setTimeout(() => {
      void fetch(`${apiBaseUrl}/api/topics`, {
        credentials: "include",
      }).then(async (response) => {
        if (!response.ok) throw new Error("catalog unavailable");
        const topics = (await response.json()) as Topic[];
        const available = generatorCandidates(topics);
        const all = catalogGenerators(topics);
        setCatalog(all);
        setCandidates(available);
        const requested = all.find(item=>item.skillId===requestedSkill);
        if (requested) {
          setSkill(requested.skillId);setCollection(available.some(a=>a.skillId===requested.skillId && a.subject==="mathematik")?"regular":"demo");
          setGrade(String(requested.grade));setSubject(requested.subject??"");setQuery("");
        }
        else if (requestedSkill) {
          setSkill("");
          setMessage("Diese Kompetenz ist hier nicht verfügbar. Bitte wähle eine verfügbare Kompetenz aus.");
        }
        else if (!requestedSkill) setSkill(filterChoices(available.filter(a=>a.subject==="mathematik"),"","","")[0]?.skillId ?? "");
        setCatalogState("ready");
      }).catch(() => setCatalogState("error"));
      void fetch(`${apiBaseUrl}/api/learners`, {
        credentials: "include",
      }).then(async (response) => {
        if (response.ok) setLearners((await response.json()) as Learner[]);
      }).catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(initialLoad);
  }, [requestedSkill]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(undefined);
    const form = new FormData(event.currentTarget);
    if (!pdfOutputs.length) {
      setOutputMessage("Mindestens eine PDF-Ausgabe muss ausgewählt bleiben.");setBusy(false);return;
    }
    if (catalogState !== "ready" || !choices.some((item) => item.skillId === skill) || !validSeed(seed)) {
      setMessage("Bitte wähle eine verfügbare Kompetenz und eine ganze Variantennummer von 0 bis 9223372036854775807.");
      setBusy(false);
      return;
    }
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/generation-jobs`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": decodeURIComponent(cookie("lernwerk_csrf") ?? ""),
          },
          body: JSON.stringify({
            skill_id: form.get("skill"),
            seed: seed,
            difficulty: Number(form.get("difficulty")),
            pdf_outputs: pdfOutputs,
            learner_profile_id: form.get("learner_profile_id") || null,
            ...candidateSelectors(String(form.get("skill")), candidates),
          }),
        },
      );
      if (response.status === 401 || response.status === 403) {
        setMessage("Bitte melde dich zuerst an, damit dein Auftrag sicher zugeordnet werden kann.");
      } else if (response.status === 422 || response.status === 400 || response.status === 409) {
        setMessage("Diese Auswahl ist nicht mehr gültig. Bitte lade den Katalog neu und prüfe deine Angaben.");
      } else if (response.status === 429) {
        setMessage("Zurzeit sind zu viele Aufträge gestartet. Bitte warte etwas und versuche es erneut.");
      } else if (!response.ok) {
        setMessage("Der Auftrag konnte nicht gestartet werden. Bitte versuche es gleich noch einmal.");
      } else {
        await response.json();
        router.push("/my-worksheets");
      }
    } catch {
      setMessage(
        "Die Verbindung zur Lernwerk-Werkstatt ist gerade unterbrochen. Bitte versuche es gleich noch einmal.",
      );
    } finally {
      setBusy(false);
    }
  }

  function chooseNewSeed() {
    const randomValue = window.crypto?.getRandomValues(new Uint32Array(1))[0] ?? Math.random() * 2 ** 31;
    setSeed(String(Math.floor(randomValue % 2 ** 31)));
  }

  function chooseOutput(kind: PdfOutput, checked: boolean) {
    const next=togglePdfOutput(pdfOutputs,kind,checked);
    setOutputMessage(!checked && pdfOutputs.length===1 ? "Mindestens eine PDF-Ausgabe muss ausgewählt bleiben." : undefined);
    setPdfOutputs(next);
  }

  return (
    <form className="generator-card" onSubmit={submit}>
      <label>Aufgabenangebot<select aria-label="Aufgabenangebot" value={collection} onChange={event=>{setCollection(event.target.value as "regular"|"demo");setSkill("");setQuery("");setSubject("");setGrade("");}}>
        <option value="regular">Mathematik · Klasse 1–10</option><option value="demo">Grundschule und weitere Fächer</option>
      </select></label>
      {collection === "demo" ? <p className="quality-note">Vorhandene Übungstypen aus dem Themenkatalog. Die Auswahl berücksichtigt die tatsächliche Verfügbarkeit.</p> : null}
      <div className="catalog-filters">
        <label>Kompetenz suchen<input type="search" value={query} placeholder="Zum Beispiel: Brüche oder Volumen" onChange={(event) => {setQuery(event.target.value); setSkill("");}} /></label>
        <div className="form-row">
          <label>Fach<select aria-label="Fach" value={subject} onChange={(event) => {setSubject(event.target.value); setSkill("");}}>
            <option value="">Alle Fächer</option><option value="mathematik">Mathematik</option><option value="deutsch">Deutsch</option><option value="englisch">Englisch</option><option value="sachunterricht">Sachunterricht</option><option value="physik">Physik</option>
          </select></label>
          <label>Klasse<select aria-label="Klasse" value={grade} onChange={(event) => {setGrade(event.target.value); setSkill("");}}>
            <option value="">Alle Klassen</option>{Array.from({length:10},(_,i)=>i+1).map((g)=><option key={g} value={g}>Klasse {g}</option>)}
          </select></label>
        </div>
        {query || grade || subject ? <button className="finder-reset" type="button" onClick={() => {setQuery("");setGrade("");setSubject("");}}>Filter zurücksetzen</button> : null}
        <p role="status">{catalogState === "loading" ? "Katalog wird geladen …" : catalogState === "error" ? "Katalog nicht erreichbar. Bitte lade diese Seite erneut." : `${visible.length} ${visible.length === 1 ? "verfügbare Kompetenz" : "verfügbare Kompetenzen"}`}</p>
      </div>
      <label>
        Kompetenz
        <select name="skill" aria-label="Kompetenz" required value={skill} onChange={(event) => setSkill(event.target.value)} aria-describedby="selected-competency">
          <option value="">{visible.length ? "Bitte Kompetenz auswählen" : "Keine passende Kompetenz – Filter ändern"}</option>
          {visible.map((item) => <option key={item.skillId} value={item.skillId}>{item.label} · Klasse {item.grade}</option>)}
        </select>
      </label>
      <p id="selected-competency" className="selected-competency">{choices.find((item) => item.skillId === skill)?.label}</p>
      {selected ? (
        <details className="version-note"><summary>Technische Details · feste Aufgaben- und Druckversion</summary><p>Kompetenz {selected.skillId}<br />Generator {selected.generatorVersion}<br />Design {selected.designVersion}</p><p>Gilt nur für diesen neuen Auftrag. Gespeicherte Blätter behalten ihre Versionen.</p></details>
      ) : null}
      {skill ? <aside className={`stage-guide stage-${stage.id}`}>
        <p>{stage.shortLabel}</p>
        <strong>{stage.label}</strong>
        <span>{stage.promise}</span>
      </aside> : null}
      {learners.length ? <label>
        Für wen ist das Blatt?
        <select name="learner_profile_id" defaultValue="">
          <option value="">Ohne Profil erstellen</option>
          {learners.map((learner) => <option key={learner.id} value={learner.id}>{learner.display_name}{learner.grade ? ` · Klasse ${learner.grade}` : ""}</option>)}
        </select>
      </label> : null}
      <div className="form-row">
        <label>
          Schwierigkeit
          <select name="difficulty" aria-label="Schwierigkeit" defaultValue="2">
            {[1, 2, 3, 4, 5].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Variantennummer
          <input name="seed" inputMode="numeric" pattern="[0-9]{1,19}" value={seed} onChange={(event) => setSeed(event.target.value)} required />
        </label>
        <button className="button secondary seed-button" type="button" onClick={chooseNewSeed}>
          Neue Variante würfeln
        </button>
      </div>
      <p className="quality-note">Dieselbe Variantennummer erzeugt bei gleicher Kompetenz und Schwierigkeit dieselbe Aufgabenfolge. „Neue Variante würfeln“ wählt eine andere Nummer für neue Aufgaben.</p>
      <fieldset className="pdf-output-selection" aria-describedby="pdf-output-hint pdf-output-status">
        <legend>Du bekommst</legend>
        <label className="check">
          <input type="checkbox" name="pdf_outputs" value="task_pdf" checked={pdfOutputs.includes("task_pdf")} onChange={event=>chooseOutput("task_pdf",event.target.checked)} /> Aufgaben-PDF
        </label>
        <label className="check">
          <input type="checkbox" name="pdf_outputs" value="solution_pdf" checked={pdfOutputs.includes("solution_pdf")} onChange={event=>chooseOutput("solution_pdf",event.target.checked)} /> Lösungs-PDF
        </label>
      </fieldset>
      <p className="quality-note" id="pdf-output-hint">Wähle mindestens einen PDF-Download. Intern werden beide PDFs erstellt; bei diesem Auftrag werden dir nur die gewählten Downloads angeboten.</p>
      <p className="form-message" id="pdf-output-status" role="status" hidden={!outputMessage}>{outputMessage}</p>
      <p className="quality-note">Aufgabenanzahl und Seitenaufteilung sind für die gewählte Kompetenz festgelegt. Eine eigene Umfangsauswahl oder dynamische Vorschau ist hier nicht verfügbar.</p>
      <button className="button primary" disabled={busy || catalogState !== "ready" || !skill}>
        {busy ? "Wird vorbereitet …" : "Mein Arbeitsblatt erstellen"}
      </button>
      {message && <p className="form-message" role="status">{message}</p>}
    </form>
  );
}
